<template>
  <!-- Overlay de surbrillance pour les éléments data-i18n (teleport dans body) -->
  <Teleport to="body">
    <div
      v-if="highlightMode && highlightedCode"
      class="i18n-highlight-overlay"
      :style="highlightStyle"
      @click.stop="onHighlightClick"
    >
      <span class="i18n-highlight-code">{{ highlightedCode }}</span>
    </div>
  </Teleport>

  <q-dialog v-model="show">
    <q-card style="width: 1400px; max-width: 95vw">
      <q-card-section>
        <div class="text-h6" data-i18n="app.translation.quickEdit.title">
          {{ this.$t('app.translation.quickEdit.title') }}
          <q-btn
            round
            flat
            dense
            icon="close"
            class="float-right"
            color="grey-8"
            @click="doClose"
          ></q-btn>
        </div>
      </q-card-section>
      <q-separator inset></q-separator>

      <q-card-section class="q-pt-none">
        <q-splitter v-model="splitterModel" style="height: 500px;">
          <template v-slot:before>
            <div class="q-pa-sm q-gutter-md">
              <!-- Code -->
              <q-input
                color="app"
                dense
                outlined
                v-model="formData.codeSuffix"
                :hint="$t('app.translation.quickEdit.codeHint')"
                data-i18n="app.translation.quickEdit.codeHint"
                class="code-big-cell"
                @update:model-value="onCodeChanged"
              >
                <template v-if="formData.codePrefix" #prepend>
                  <q-item-label class="code-big-cell">{{ formData.codePrefix }}.</q-item-label>
                </template>
              </q-input>

              <!-- Liste des langues -->
              <div class="text-subtitle2 q-mt-md q-mb-sm" data-i18n="app.translation.quickEdit.translationsByLang">
                {{ $t('app.translation.quickEdit.translationsByLang') }}
              </div>

              <q-list bordered separator class="rounded-borders languages-list">
                <q-item v-for="lang in availableLangs" :key="lang.code" class="lang-item" dense>
                  <q-item-section side>
                    <q-img
                      :src="'https://flagcdn.com/h20/' + getLangFlag(lang) + '.png'"
                      style="width: 24px; height: 18px; border: 1px solid #ddd; border-radius: 2px;"
                    >
                      <q-tooltip>{{ lang.name }}</q-tooltip>
                    </q-img>
                  </q-item-section>

                  <q-item-section>
                    <!-- Si traduction existe ou en mode édition -->
                    <template v-if="langMessages[lang.code]?.editing || langMessages[lang.code]?.message">
                      <q-input
                        v-model="langMessages[lang.code].message"
                        :label="lang.name"
                        dense
                        color="app"
                        type="textarea"
                        autogrow
                        :input-style="{ minHeight: '36px' }"
                        @update:model-value="onLangMessageChanged(lang.code)"
                      />
                    </template>
                    <!-- Si traduction manquante -->
                    <template v-else>
                      <div class="text-grey-6 text-italic" data-i18n="app.translation.quickEdit.missingTranslation">
                        {{ $t('app.translation.quickEdit.missingTranslation') }}
                      </div>
                    </template>
                  </q-item-section>

                  <q-item-section side>
                    <!-- Bouton save si modifié -->
                    <q-btn
                      v-if="langMessages[lang.code]?.modified"
                      icon="save"
                      color="positive"
                      flat
                      dense
                      round
                      :loading="langMessages[lang.code]?.saving"
                      @click="saveTranslationForLang(lang.code)"
                      data-i18n="common.save"
                    >
                      <q-tooltip>{{ $t('common.save') }}</q-tooltip>
                    </q-btn>
                    <!-- Bouton + si traduction manquante et pas en édition -->
                    <q-btn
                      v-else-if="!langMessages[lang.code]?.message && !langMessages[lang.code]?.editing"
                      icon="add"
                      color="app"
                      flat
                      dense
                      round
                      @click="startAddingTranslation(lang.code)"
                      data-i18n="app.translation.quickEdit.addTranslation"
                    >
                      <q-tooltip>{{ $t('app.translation.quickEdit.addTranslation') }}</q-tooltip>
                    </q-btn>
                  </q-item-section>
                </q-item>
              </q-list>
            </div>
          </template>

          <template v-slot:separator>
            <q-avatar v-show="editMode" color="app" text-color="white" size="40px" icon="drag_indicator"/>
          </template>

          <template v-slot:after>
            <div class="q-pa-sm related-translations-section">
              <generic-title :title="this.$t('app.translation.quickEdit.relatedTranslations')" style="padding-top: 0px" data-i18n="app.translation.quickEdit.relatedTranslations">
                <template v-slot:right-actions>
                  <!-- Filtres par langue -->
                  <div v-if="relatedLocales.length > 0" class="row justify-between full-width">
                    <q-chip
                      v-for="lang in relatedLocales"
                      :key="lang.code || lang"
                      :color="selectedLocaleFilter === (lang.code || lang) ? 'primary' : 'grey-4'"
                      :text-color="selectedLocaleFilter === (lang.code || lang) ? 'white' : 'grey-8'"
                      clickable
                      size="md"
                      @click="toggleLocaleFilter(lang.code || lang)"
                    >
                      {{ (lang.code || lang).toUpperCase() }}
                      <q-badge
                        v-if="getTranslationCountByLocale(lang.code || lang) > 0"
                        :color="selectedLocaleFilter === (lang.code || lang) ? 'white' : 'primary'"
                        :text-color="selectedLocaleFilter === (lang.code || lang) ? 'negative' : 'white'"
                        floating
                        rounded
                      >
                        {{ getTranslationCountByLocale(lang.code || lang) }}
                      </q-badge>
                    </q-chip>
                    <q-chip
                      v-if="selectedLocaleFilter"
                      color="grey-3"
                      text-color="grey-7"
                      clickable
                      size="md"
                      icon="clear"
                      @click="selectedLocaleFilter = null"
                      data-i18n="common.all"
                    >
                      {{ $t('common.all') }}
                    </q-chip>
                  </div>
                </template>
              </generic-title>

              <!-- Table des traductions -->
              <q-table
                v-if="hasRelatedTranslations"
                :rows="relatedTableRows"
                :columns="relatedTableColumns"
                row-key="id"
                dense
                flat
                bordered
                :rows-per-page-options="[0]"
                hide-pagination
                :loading="loadingRelated"
                class="related-table"
                virtual-scroll
                :virtual-scroll-sticky-size-start="28"
                @row-click="onTableRowClick"
              >
                <template v-slot:body-cell-lang="props">
                  <q-td :props="props">
                    <q-badge :color="props.row.lang === 'fr' ? 'blue' : 'green'">
                      {{ props.row.lang.toUpperCase() }}
                    </q-badge>
                  </q-td>
                </template>
                <template v-slot:body-cell-service="props">
                  <q-td :props="props">
                    <q-badge color="grey-6" outline>
                      {{ (props.row.service?.code || props.row.service || '').toUpperCase() }}
                    </q-badge>
                  </q-td>
                </template>
                <template v-slot:body-cell-code="props">
                  <q-td :props="props" class="code-cell">
                    {{ props.row.code }}
                  </q-td>
                </template>
                <template v-slot:body-cell-message="props">
                  <q-td :props="props">
                    <span v-if="props.row.message">{{ props.row.message }}</span>
                    <span v-else class="text-orange" data-i18n="app.translation.quickEdit.missingTranslation">
                      <q-icon name="warning" size="xs"/>
                      {{ $t('app.translation.quickEdit.missingTranslation') }}
                    </span>
                  </q-td>
                </template>
                <template v-slot:body-cell-actions="props">
                  <q-td :props="props">
                    <q-btn
                      icon="delete"
                      color="negative"
                      flat
                      dense
                      round
                      size="sm"
                      @click.stop="confirmDeleteTranslation(props.row)"
                      data-i18n="common.delete"
                    >
                      <q-tooltip>{{ $t('common.delete') }}</q-tooltip>
                    </q-btn>
                  </q-td>
                </template>
              </q-table>

              <!-- État vide ou chargement -->
              <div v-else-if="loadingRelated" class="text-center q-pa-md">
                <q-spinner color="app" size="2em"/>
              </div>
              <div v-else class="text-center text-grey-6 q-pa-md" data-i18n="app.translation.quickEdit.noRelatedTranslations">
                <q-icon name="info" size="sm" class="q-mb-xs"/>
                <div class="text-caption">{{ $t('app.translation.quickEdit.noRelatedTranslations') }}</div>
              </div>
            </div>
          </template>
        </q-splitter>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn
          flat
          :label="$t('common.close')"
          @click="doClose"
          color="grey-8"
          data-i18n="common.close"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import AxiosService from '@core/services/app/axiosService';
import authHeader from '@core/services/app/authHeader';
import appNotificationService from "@core/services/utils/appNotificationService";
import AppTranslationService from "@core/services/app/appTranslationService";
import GenericTitle from "@core/components/title/genericTitle.vue";
import { useAuthStore } from '@core/stores/authStore';

export default {
  name: 'app-translation-quick-edit',
  components: {GenericTitle},

  data() {
    return {
      show: false,
      searching: false,
      splitterModel: 30,
      formData: {
        codePrefix: '',
        codeSuffix: ''
      },
      // Langues disponibles (depuis APP_LANGUAGE)
      availableLangs: [],
      // Messages par langue: { fr: { message: '', originalMessage: '', modified: false, saving: false, editing: false, id: null }, ... }
      langMessages: {},
      searchTimeout: null,
      // Mode détection des éléments data-i18n
      highlightMode: false,
      highlightedElement: null,
      highlightedCode: '',
      highlightStyle: {
        top: '0px',
        left: '0px',
        width: '0px',
        height: '0px'
      },
      // Traductions associées (liste simple)
      relatedTranslations: [],
      relatedLocales: [],
      relatedPrefix: '',
      loadingRelated: false,
      selectedLocaleFilter: null,
      relatedSearchTimeout: null
    };
  },

  computed: {
    hasRelatedTranslations() {
      return this.relatedTranslations.length > 0;
    },
    fullCode() {
      if (!this.formData.codePrefix) {
        return this.formData.codeSuffix || '';
      }
      return this.formData.codePrefix + '.' + (this.formData.codeSuffix || '');
    },
    filteredLocales() {
      if (this.selectedLocaleFilter) {
        return [this.selectedLocaleFilter];
      }
      return this.relatedLocales;
    },
    relatedTableColumns() {
      return [
        {
          name: 'lang',
          label: this.$t('app.translation.quickEdit.lang'),
          i18nKey: 'app.translation.quickEdit.lang',
          field: 'lang',
          align: 'center',
          sortable: true,
          style: 'width: 50px'
        },
        {
          name: 'service',
          label: this.$t('app.translation.quickEdit.service'),
          i18nKey: 'app.translation.quickEdit.service',
          field: row => row.service?.code || row.service,
          align: 'center',
          sortable: true,
          style: 'width: 60px'
        },
        {
          name: 'code',
          label: this.$t('app.translation.quickEdit.code'),
          i18nKey: 'app.translation.quickEdit.code',
          field: 'code',
          align: 'left',
          sortable: true,
          style: 'width: 180px'
        },
        {
          name: 'message',
          label: this.$t('app.translation.quickEdit.message'),
          i18nKey: 'app.translation.quickEdit.message',
          field: 'message',
          align: 'left',
          sortable: true
        },
        {
          name: 'actions',
          label: '',
          align: 'center',
          style: 'width: 40px'
        }
      ];
    },
    relatedTableRows() {
      if (!this.relatedTranslations || this.relatedTranslations.length === 0) {
        return [];
      }

      let filtered = this.relatedTranslations;
      if (this.selectedLocaleFilter) {
        filtered = this.relatedTranslations.filter(trans => {
          const langCode = trans.lang?.code || trans.lang;
          return langCode === this.selectedLocaleFilter;
        });
      }

      return filtered.map(trans => {
        const langCode = trans.lang?.code || trans.lang;
        return {
          id: `${trans.id}-${langCode}`,
          originalId: trans.id,
          lang: langCode,
          code: trans.code,
          message: trans.message,
          service: trans.service
        };
      });
    }
  },

  methods: {

    open(selectedText) {
      this.resetForm();
      this.setCodeFromFull(selectedText || '');
      this.show = true;

      if (selectedText) {
        this.searchTranslationsForCode();
        this.searchRelatedTranslations();
      }
    },

    setCodeFromFull(fullCode) {
      if (!fullCode) {
        this.formData.codePrefix = '';
        this.formData.codeSuffix = '';
        return;
      }
      const firstDot = fullCode.indexOf('.');
      if (firstDot >= 3) {
        this.formData.codePrefix = fullCode.substring(0, firstDot);
        this.formData.codeSuffix = fullCode.substring(firstDot + 1);
      } else if (fullCode.length >= 3) {
        // Pas de point mais au moins 3 caractères
        this.formData.codePrefix = fullCode.substring(0, 3);
        this.formData.codeSuffix = fullCode.substring(3);
      } else {
        // Moins de 3 caractères, on met tout en préfixe
        this.formData.codePrefix = fullCode;
        this.formData.codeSuffix = '';
      }
    },

    resetForm() {
      this.formData = {
        codePrefix: '',
        codeSuffix: ''
      };
      this.initLangMessages();
      this.searching = false;
      this.relatedTranslations = [];
      this.relatedLocales = [];
      this.relatedPrefix = '';
      this.loadingRelated = false;
      this.selectedLocaleFilter = null;
    },

    initLangMessages() {
      // Initialise la structure langMessages pour chaque langue disponible
      const messages = {};
      this.availableLangs.forEach(lang => {
        messages[lang.code] = {
          message: '',
          originalMessage: '',
          modified: false,
          saving: false,
          editing: false,
          id: null
        };
      });
      this.langMessages = messages;
    },

    doClose() {
      this.show = false;
      this.resetForm();
    },

    onCodeChanged(value) {
      this.searchTranslationsForCode();
      this.searchRelatedTranslations();
    },

    onLangMessageChanged(langCode) {
      if (this.langMessages[langCode]) {
        const current = this.langMessages[langCode].message || '';
        const original = this.langMessages[langCode].originalMessage || '';
        // Force reactivity by replacing the object
        this.langMessages[langCode] = {
          ...this.langMessages[langCode],
          modified: current !== original
        };
      }
    },

    startAddingTranslation(langCode) {
      if (this.langMessages[langCode]) {
        // Force reactivity by replacing the object
        this.langMessages[langCode] = {
          ...this.langMessages[langCode],
          editing: true,
          message: ''
        };
      }
    },

    getLangFlag(lang) {
      const code = lang.code || lang;
      const mapping = {'en': 'gb'};
      return mapping[code] || code;
    },

    async searchRelatedTranslations() {
      if (!this.fullCode) {
        this.relatedTranslations = [];
        this.relatedLocales = [];
        this.relatedPrefix = '';
        return;
      }

      if (this.relatedSearchTimeout) {
        clearTimeout(this.relatedSearchTimeout);
      }

      this.relatedSearchTimeout = setTimeout(async () => {
        this.loadingRelated = true;
        try {
          const response = await AxiosService.post('/appTranslation/findByPrefix', {
            prefix: this.fullCode
          }, {
            headers: authHeader()
          });

          if (response.data) {
            this.relatedTranslations = response.data.translations || [];
            this.relatedLocales = response.data.langs || [];
            this.relatedPrefix = response.data.prefix || '';
          }
        } catch (error) {
          console.error('[TranslationQuickEdit] Error fetching related translations:', error);
          this.relatedTranslations = [];
          this.relatedLocales = [];
        } finally {
          this.loadingRelated = false;
        }
      }, 500);
    },

    getTranslationCountByLocale(lang) {
      if (!this.relatedTranslations || this.relatedTranslations.length === 0) {
        return 0;
      }
      return this.relatedTranslations.filter(trans => {
        const langCode = trans.lang?.code || trans.lang;
        return langCode === lang;
      }).length;
    },

    toggleLocaleFilter(lang) {
      if (this.selectedLocaleFilter === lang) {
        this.selectedLocaleFilter = null;
      } else {
        this.selectedLocaleFilter = lang;
      }
    },

    onTableRowClick(evt, row) {
      // Charge le code sélectionné et recherche les traductions pour toutes les langues
      this.setCodeFromFull(row.code);
      this.searchTranslationsForCode();
    },

    async searchTranslationsForCode() {
      if (!this.fullCode) {
        this.initLangMessages();
        return;
      }

      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(async () => {
        this.searching = true;
        try {
          const response = await AxiosService.post('/appTranslation/findByCode', {
            code: this.fullCode
          }, {
            headers: authHeader()
          });

          // Réinitialise les messages
          this.initLangMessages();

          if (response.data && response.data.translations) {
            // Remplit les messages pour chaque langue trouvée
            response.data.translations.forEach(trans => {
              const langCode = trans.lang?.code || trans.lang;
              if (this.langMessages[langCode]) {
                this.langMessages[langCode] = {
                  message: trans.message || '',
                  originalMessage: trans.message || '',
                  modified: false,
                  saving: false,
                  editing: false,
                  id: trans.id
                };
              }
            });
          }
        } catch (error) {
          console.error('[TranslationQuickEdit] Error fetching translations:', error);
          this.initLangMessages();
        } finally {
          this.searching = false;
        }
      }, 300);
    },

    async saveTranslationForLang(langCode) {
      if (!this.fullCode || !this.langMessages[langCode]) {
        return;
      }

      // Force reactivity
      this.langMessages[langCode] = {
        ...this.langMessages[langCode],
        saving: true
      };

      try {
        const langModel = this.availableLangs.find(l => l.code === langCode);
        const payload = {
          code: this.fullCode,
          lang: langModel || {code: langCode},
          message: this.langMessages[langCode].message
        };

        if (this.langMessages[langCode].id) {
          payload.id = this.langMessages[langCode].id;
        }

        const translation = await AppTranslationService.addOrUpdate(payload);

        if (translation) {
          // Force reactivity
          this.langMessages[langCode] = {
            ...this.langMessages[langCode],
            id: translation.id,
            originalMessage: this.langMessages[langCode].message,
            modified: false,
            editing: false,
            saving: false
          };

          // Rafraîchit les traductions associées
          this.searchRelatedTranslations();
        }
      } catch (error) {
        appNotificationService.pushError(error);
        // Force reactivity
        this.langMessages[langCode] = {
          ...this.langMessages[langCode],
          saving: false
        };
      }
    },

    confirmDeleteTranslation(row) {
      this.$q.dialog({
        title: this.$t('common.confirm'),
        message: this.$t('app.translation.quickEdit.confirmDelete', {code: row.code, lang: row.lang.toUpperCase()}),
        cancel: {
          label: this.$t('common.cancel'),
          flat: true
        },
        ok: {
          label: this.$t('common.delete'),
          color: 'negative'
        },
        persistent: true
      }).onOk(() => {
        this.deleteTranslation(row);
      });
    },

    async deleteTranslation(row) {
      try {
        await AppTranslationService.delete({id: row.originalId});

        // Rafraîchit les traductions associées
        this.searchRelatedTranslations();

        // Si le code supprimé est celui actuellement édité, réinitialise le formulaire de gauche
        if (row.code === this.fullCode) {
          const langCode = row.lang;
          if (this.langMessages[langCode]) {
            this.langMessages[langCode] = {
              message: '',
              originalMessage: '',
              modified: false,
              saving: false,
              editing: false,
              id: null
            };
          }
        }
      } catch (error) {
        appNotificationService.pushError(error);
      }
    },

    /** Droit global « Édition des traductions » (APP_GLOBAL_TRANSLATE) */
    canTranslate() {
      return useAuthStore().hasPermission('APP_GLOBAL_TRANSLATE')
    },

    handleKeyDown(event) {
      if (!this.canTranslate()) return;
      // Alt+T pour ouvrir l'éditeur de traduction
      if (event.altKey && event.ctrlKey && (event.key === 't' || event.key === 'T')) {
        event.preventDefault();
        event.stopPropagation();

        // Si on est déjà en mode highlight avec un élément sélectionné, ouvrir l'éditeur
        if (this.highlightMode && this.highlightedCode) {
          this.open(this.highlightedCode);
          this.exitHighlightMode();
          return;
        }

        const selectedText = window.getSelection().toString().trim();
        console.log('[TranslationQuickEdit] Alt+T pressed, selected text:', selectedText);
        this.open(selectedText);
      }

      // Alt seul pour activer/désactiver le mode détection
      if (event.key === 'Alt' && event.ctrlKey) {
        console.log('[TranslationQuickEdit] Ctrl+Alt pressed, highlightMode:', this.highlightMode);
        if (!this.highlightMode) {
          this.enterHighlightMode();
        }
      }

      // Escape pour quitter le mode highlight
      if (event.key === 'Escape' && this.highlightMode) {
        this.exitHighlightMode();
      }
    },

    handleKeyUp(event) {
      // Quitter le mode highlight quand Alt est relâché
      if ((event.key === 'Alt' || event.ctrlKey) && this.highlightMode) {
        // On garde le mode actif si un élément est survolé pour permettre Alt+T
        // Sinon on quitte
        if (!this.highlightedElement) {
          this.exitHighlightMode();
        }
      }
    },

    enterHighlightMode() {
      console.log('[TranslationQuickEdit] Entering highlight mode');
      this.highlightMode = true;
      document.body.classList.add('i18n-highlight-active');
      document.addEventListener('mousemove', this.handleMouseMove);
    },

    exitHighlightMode() {
      console.log('[TranslationQuickEdit] Exiting highlight mode');
      this.highlightMode = false;
      this.highlightedElement = null;
      this.highlightedCode = '';
      document.body.classList.remove('i18n-highlight-active');
      document.removeEventListener('mousemove', this.handleMouseMove);
    },

    handleMouseMove(event) {
      if (!this.highlightMode) return;

      const element = event.target.closest('[data-i18n]');
      console.log('[TranslationQuickEdit] MouseMove, target:', event.target.tagName, 'found data-i18n:', element ? element.dataset.i18n : 'none');

      if (element) {
        if (element !== this.highlightedElement) {
          this.highlightedElement = element;
          this.highlightedCode = element.dataset.i18n;
          this.updateHighlightPosition(element);
          console.log('[TranslationQuickEdit] Highlighting:', this.highlightedCode);
        }
      } else {
        // Chercher dans les parents
        let parent = event.target;
        while (parent && parent !== document.body) {
          if (parent.dataset && parent.dataset.i18n) {
            if (parent !== this.highlightedElement) {
              this.highlightedElement = parent;
              this.highlightedCode = parent.dataset.i18n;
              this.updateHighlightPosition(parent);
            }
            return;
          }
          parent = parent.parentElement;
        }

        // Aucun élément trouvé
        if (this.highlightedElement) {
          this.highlightedElement = null;
          this.highlightedCode = '';
        }
      }
    },

    updateHighlightPosition(element) {
      const rect = element.getBoundingClientRect();
      // Position fixed = relative au viewport, donc pas besoin de scroll offset
      this.highlightStyle = {
        top: `${rect.top}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`
      };
    },

    onHighlightClick() {
      if (this.highlightedCode) {
        this.open(this.highlightedCode);
        this.exitHighlightMode();
      }
    },

    async getLocales() {
      try {
        const response = await AppTranslationService.getAllLangs();
        if (Array.isArray(response)) {
          this.availableLangs = response;
        } else if (response && response.langs) {
          this.availableLangs = response.langs;
        }
        this.initLangMessages();
      } catch (error) {
        console.error('[TranslationQuickEdit] Error fetching locales:', error);
      }
    }
  },

  mounted() {
    console.log('[TranslationQuickEdit] Component mounted');
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('keyup', this.handleKeyUp);
    this.getLocales();
  },

  beforeUnmount() {
    console.log('[TranslationQuickEdit] Component unmounting');
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('keyup', this.handleKeyUp);
    document.removeEventListener('mousemove', this.handleMouseMove);
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    if (this.relatedSearchTimeout) {
      clearTimeout(this.relatedSearchTimeout);
    }
  }
};
</script>

<style>
/* Styles globaux pour le mode highlight */
.i18n-highlight-active [data-i18n] {
  outline: 2px dashed rgba(25, 118, 210, 0.5) !important;
  outline-offset: 2px;
  cursor: pointer !important;
}

.i18n-highlight-active [data-i18n]:hover {
  outline-color: rgba(25, 118, 210, 1) !important;
  background-color: rgba(25, 118, 210, 0.1) !important;
}
</style>

<style>
/* Styles pour l'overlay (non-scoped car teleporté dans body) */
.i18n-highlight-overlay {
  position: fixed;
  pointer-events: auto;
  z-index: 99999;
  border: 2px solid #1976d2;
  background-color: rgba(25, 118, 210, 0.15);
  cursor: pointer;
  transition: all 0.1s ease;
}

.i18n-highlight-code {
  position: absolute;
  top: -24px;
  left: 0;
  background-color: #1976d2;
  color: white;
  font-size: 11px;
  font-family: monospace;
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
</style>

<style scoped>
.related-translations-section {
  background: #f8f9fa;
  border-radius: 8px;
  height: 100%;
  overflow-y: auto;
}
/* Dialogue téléporté hors .adm-page → overrides dark mode explicites. */
.body--dark .related-translations-section {
  background: rgba(255, 255, 255, 0.04);
}

.related-table {
  max-height: 420px;
  overflow-y: auto;
}

.related-table :deep(.q-table__top),
.related-table :deep(.q-table__bottom) {
  padding: 4px 8px;
}

.related-table :deep(.q-table__middle) {
  max-height: 380px;
}

.related-table :deep(tbody tr) {
  cursor: pointer;
}

.related-table :deep(tbody tr:hover) {
  background-color: rgba(25, 118, 210, 0.08);
}

.code-cell {
  font-family: monospace;
  font-size: 14px;
  color: #666;
}

.code-big-cell {
  font-family: monospace;
  font-size: 24px;
  color: #666;
}
.body--dark .code-cell,
.body--dark .code-big-cell {
  color: #aeb8c7;
}

.languages-list {
  max-height: 420px;
  overflow-y: auto;
}

.lang-item {
  min-height: 56px;
}
</style>
